import { createFileRoute, Link } from "@tanstack/react-router";
import { Share2, ArrowLeft, ExternalLink, MessageCircle, Facebook, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { SERVICES } from "@/lib/services";
import { POSTS } from "@/lib/blog-data";
import { SITE } from "@/lib/site";
import { toast } from "sonner";

export const Route = createFileRoute("/test-share")({
  component: TestSharePage,
});

function TestSharePage() {
  const allPages = [
    { title: "Home", path: "/", description: SITE.description, image: "/og-image.jpg" },
    { title: "Sobre", path: "/sobre", description: "Conheça a história da Clínica Podomasso.", image: "/og-image.jpg" },
    { title: "Serviços", path: "/servicos", description: "Nossos tratamentos especializados.", image: "/og-image.jpg" },
    { title: "Blog", path: "/blog", description: "Conteúdo educativo sobre podologia.", image: "/og-image.jpg" },
    ...SERVICES.map(s => ({ 
      title: s.title, 
      path: `/servicos/${s.slug}`, 
      description: s.metaDescription, 
      image: s.image 
    })),
    ...Object.values(POSTS).map(p => ({ 
      title: p.title, 
      path: `/blog/${p.slug}`, 
      description: p.excerpt, 
      image: p.image 
    }))
  ];

  const copyLink = (path: string) => {
    navigator.clipboard.writeText(`${SITE.url}${path}`);
    toast.success("Link copiado!");
  };

  return (
    <div className="min-h-screen bg-secondary/20 py-20 px-4">
      <div className="mx-auto max-w-4xl">
        <div className="mb-10 flex items-center justify-between">
          <div>
            <Link to="/" className="flex items-center gap-2 text-primary hover:underline font-semibold mb-2">
              <ArrowLeft className="h-4 w-4" /> Voltar ao site
            </Link>
            <h1 className="font-display text-4xl font-bold">Páginas de Compartilhamento</h1>
            <p className="text-muted-foreground mt-2">
              Use esta página para testar como os links da Podomasso aparecem quando compartilhados.
            </p>
          </div>
          <Share2 className="h-12 w-12 text-primary/20" />
        </div>

        <div className="grid gap-8">
          {allPages.map((page, i) => (
            <Card key={i} className="overflow-hidden border-none shadow-elegant">
              <CardHeader className="bg-background border-b border-border/50">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-xl">{page.title}</CardTitle>
                    <CardDescription>{page.path}</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => copyLink(page.path)}>
                      <Copy className="h-4 w-4 mr-2" /> Copiar
                    </Button>
                    <Button variant="outline" size="sm" asChild>
                      <a href={`${SITE.url}${page.path}`} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4 mr-2" /> Visitar
                      </a>
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="grid md:grid-cols-2">
                  {/* Preview WhatsApp */}
                  <div className="p-6 bg-[#E5DDD5]/50 border-r border-border/30">
                    <p className="text-[10px] font-bold text-muted-foreground uppercase mb-4 tracking-widest">
                      Preview no WhatsApp
                    </p>
                    <div className="bg-white rounded-lg overflow-hidden shadow-sm max-w-[300px]">
                      <img 
                        src={page.image.startsWith('http') ? page.image : `${SITE.url}${page.image}`} 
                        alt="Preview" 
                        className="w-full aspect-[1.91/1] object-cover"
                      />
                      <div className="p-3 border-t border-border/10 bg-secondary/10">
                        <h4 className="font-bold text-sm line-clamp-1">{page.title}</h4>
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                          {page.description}
                        </p>
                        <p className="text-[10px] text-muted-foreground mt-2 uppercase tracking-tight">
                          {SITE.url.replace('https://', '')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Ações Rápidas */}
                  <div className="p-6 flex flex-col justify-center gap-4 bg-background">
                    <p className="text-sm font-semibold text-foreground">Testar compartilhamento direto:</p>
                    <div className="grid grid-cols-2 gap-3">
                      <Button 
                        variant="outline" 
                        className="border-[#25D366]/30 bg-[#25D366]/5 hover:bg-[#25D366]/10 text-[#25D366] font-bold"
                        onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`${page.title} - ${SITE.url}${page.path}`)}`, '_blank')}
                      >
                        <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp
                      </Button>
                      <Button 
                        variant="outline" 
                        className="border-[#1877F2]/30 bg-[#1877F2]/5 hover:bg-[#1877F2]/10 text-[#1877F2] font-bold"
                        onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`${SITE.url}${page.path}`)}`, '_blank')}
                      >
                        <Facebook className="h-4 w-4 mr-2" /> Facebook
                      </Button>
                    </div>
                    <div className="mt-4 p-4 rounded-xl bg-muted/50 border border-border/50 text-xs">
                      <p className="font-bold mb-1">Dica de SEO:</p>
                      <p className="text-muted-foreground">
                        Certifique-se de que o título tenha entre 50-60 caracteres e a descrição entre 150-160 para melhor visualização.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
